import * as React from 'react';
import { RephrasingService } from '../../features/rephrasing/RephrasingService';
import { ChatService } from '../../features/chat/ChatService';
import { AIOrchestrator } from '../../ai/orchestration/AIOrchestrator';
import { createAIProvider } from '../../ai/providers/factory';
import { EditorView } from './EditorView';
import { AskView } from './AskView';
import { ReviewView } from './ReviewView';
import { ProofreadView } from './ProofreadView';
import { AgentView } from './AgentView';
import { ProjectSelector } from './ProjectSelector';
import { CrossCheckResultsView, type MccBesidePdfLayoutChangeDetail } from './CrossCheckResultsView';
import { MasterCrossChecksInitiation } from './MasterCrossChecksInitiation';
import { Login } from './Login';
import { signOut as signOutFirebase } from '../../services/firebaseAuth';
import { SolutionsGrid, type SolutionId } from './SolutionsGrid';
import { BottomNav, NavTab } from './BottomNav';
import apiService from '../../services/api';
import { hasReviewPartyContextComplete } from '../../utils/reviewPartyContext';
import { DocumentExporter } from '../../word/utils/DocumentExporter';
import './TaskPane.css';

export type SolutionsSubView = 'list' | 'master_cross_checks' | 'construction_loan';

/** Master Cross Checks and Construction Loan share filename-based project inference (same API + UI). */
function isCrossCheckFilenameInferenceSolution(id: SolutionsSubView): boolean {
  return id === 'master_cross_checks' || id === 'construction_loan';
}

const TaskPane: React.FC = () => {
  const [viewMode, setViewMode] = React.useState<NavTab>('review');
  /** Keep Review mounted after side-ID completes so document review can finish in the background while the user is on Editor. */
  const [reviewKeepAlive, setReviewKeepAlive] = React.useState(false);
  const [solutionsSubView, setSolutionsSubView] = React.useState<SolutionsSubView>('list');
  const [selectedSubProjectId, setSelectedSubProjectId] = React.useState<string>('');
  const [selectedProjectId, setSelectedProjectId] = React.useState<string>('');
  const [refreshClausesKey, setRefreshClausesKey] = React.useState(0);
  const [isAuthenticated, setIsAuthenticated] = React.useState<boolean>(false);
  const [filenameMatchOptions, setFilenameMatchOptions] = React.useState<Array<{ project_id: string; sub_project_id: string; project_name: string; sub_project_name: string }> | null>(null);
  const [selectedFilenameMatchProjectId, setSelectedFilenameMatchProjectId] = React.useState<string | null>(null);
  const [showManualProjectSelect, setShowManualProjectSelect] = React.useState<boolean>(false);
  /** True while resolving the open document filename to a project (MCC / Construction Loan). */
  const [filenameMatchLoading, setFilenameMatchLoading] = React.useState(false);
  const [autoDetectTrigger, setAutoDetectTrigger] = React.useState(0);
  const [showMccCreateFlow, setShowMccCreateFlow] = React.useState(false);
  const [subProjectRefreshTrigger, setSubProjectRefreshTrigger] = React.useState(0);
  const [crossCheckStartError, setCrossCheckStartError] = React.useState<string | null>(null);
  /** MCC / Construction Loan: Beside SA PDF docks to the left of the task pane (full height), independent of Project Settings. */
  const [mccBesidePdfDocked, setMccBesidePdfDocked] = React.useState(false);
  const [mccBesidePdfPct, setMccBesidePdfPct] = React.useState(47);
  /** Bumped when entering MCC/CL from the solutions list so CrossCheck can scroll the workflow steps into view. */
  const [mccWorkflowScrollRequestToken, setMccWorkflowScrollRequestToken] = React.useState(0);
  const clausesViewRef = React.useRef<HTMLDivElement>(null);
  const mccPdfBesideDockRef = React.useRef<HTMLDivElement>(null);
  const mccBesideHandleSlotRef = React.useRef<HTMLDivElement>(null);
  const mccBesideRightStackRef = React.useRef<HTMLDivElement>(null);
  const pendingMccRightStackScrollRef = React.useRef<number | null>(null);
  const pendingMccClausesViewScrollRef = React.useRef<number | null>(null);

  const handleMccBesidePdfLayoutChange = React.useCallback((detail: MccBesidePdfLayoutChangeDetail) => {
    setMccBesidePdfPct(detail.pdfWidthPct);
    setMccBesidePdfDocked((prevDocked) => {
      if (detail.docked && !prevDocked) {
        // Scroll roots change when the beside column opens; CrossCheckResultsView
        // repositions the active clause card header instead of copying scrollTop.
        pendingMccRightStackScrollRef.current = null;
      } else if (!detail.docked && prevDocked) {
        // Same on close — CrossCheckResultsView aligns the clause header after undock.
        pendingMccClausesViewScrollRef.current = null;
      }
      return detail.docked;
    });
  }, []);

  React.useLayoutEffect(() => {
    if (!mccBesidePdfDocked) return;
    const y = pendingMccRightStackScrollRef.current;
    if (y == null || !mccBesideRightStackRef.current) return;
    mccBesideRightStackRef.current.scrollTop = y;
    pendingMccRightStackScrollRef.current = null;
  }, [mccBesidePdfDocked]);

  React.useLayoutEffect(() => {
    if (mccBesidePdfDocked) return;
    const y = pendingMccClausesViewScrollRef.current;
    if (y == null || !clausesViewRef.current) return;
    clausesViewRef.current.scrollTop = y;
    pendingMccClausesViewScrollRef.current = null;
  }, [mccBesidePdfDocked]);

  /** Solutions grid and MCC/CL share `.clauses-view`; scroll position persists across sub-views. */
  const prevSolutionsSubViewRef = React.useRef<SolutionsSubView>(solutionsSubView);
  React.useLayoutEffect(() => {
    const prev = prevSolutionsSubViewRef.current;
    prevSolutionsSubViewRef.current = solutionsSubView;

    if (viewMode !== 'clauses') return;

    const enteredCrossCheckFromList =
      prev === 'list' &&
      isCrossCheckFilenameInferenceSolution(solutionsSubView) &&
      solutionsSubView !== 'list';

    if (!enteredCrossCheckFromList) return;

    if (clausesViewRef.current) {
      clausesViewRef.current.scrollTop = 0;
    }
    if (mccBesideRightStackRef.current) {
      mccBesideRightStackRef.current.scrollTop = 0;
    }
    setMccWorkflowScrollRequestToken((t) => t + 1);
  }, [solutionsSubView, viewMode]);

  React.useEffect(() => {
    if (solutionsSubView === 'list' || !isCrossCheckFilenameInferenceSolution(solutionsSubView)) {
      setMccBesidePdfDocked(false);
    }
  }, [solutionsSubView]);
  const autoDetectAttemptedRef = React.useRef(false);
  /** After gating Ask/Agent/Editor, return here once Review party context is saved. */
  const pendingReturnTabRef = React.useRef<'ask' | 'agent' | 'editor' | null>(null);

  const goToReviewForContext = React.useCallback((tab: 'ask' | 'agent' | 'editor') => {
    pendingReturnTabRef.current = tab;
    setReviewKeepAlive(true);
    setViewMode('review');
  }, []);

  const handleReviewContextCommitted = React.useCallback(() => {
    setReviewKeepAlive(true);
    const next = pendingReturnTabRef.current ?? 'editor';
    pendingReturnTabRef.current = null;
    setViewMode(next);
  }, []);

  const filenameMatchUniqueProjects = React.useMemo(() => {
    if (!filenameMatchOptions) return [];
    const seen = new Map<string, string>();
    filenameMatchOptions.forEach((m) => {
      if (!seen.has(m.project_id)) seen.set(m.project_id, m.project_name);
    });
    return Array.from(seen.entries()).map(([project_id, project_name]) => ({ project_id, project_name }));
  }, [filenameMatchOptions]);

  const filenameMatchSubProjects = React.useMemo(() => {
    if (!filenameMatchOptions || selectedFilenameMatchProjectId == null) return [];
    return filenameMatchOptions.filter((m) => m.project_id === selectedFilenameMatchProjectId);
  }, [filenameMatchOptions, selectedFilenameMatchProjectId]);

  // Initialize services (following layer separation)
  const aiProvider = React.useMemo(() => createAIProvider('vertex'), []);
  const aiOrchestrator = React.useMemo(() => new AIOrchestrator(aiProvider), [aiProvider]);
  const rephrasingService = React.useMemo(
    () => new RephrasingService(aiOrchestrator),
    [aiOrchestrator]
  );
  const chatService = React.useMemo(
    () => new ChatService(rephrasingService),
    [rephrasingService]
  );

  // Check authentication on mount and listen for token expiration
  React.useEffect(() => {
    const checkAuth = () => {
      setIsAuthenticated(apiService.isAuthenticated());
    };
    
    // Initial check
    checkAuth();
    
    // Listen for token expiration events
    const handleTokenExpired = () => {
      console.log('[TaskPane] Token expired event received, showing login screen');
      setIsAuthenticated(false);
    };
    
    window.addEventListener('auth:token-expired', handleTokenExpired);
    
    // Check periodically in case token is set elsewhere
    const interval = setInterval(checkAuth, 1000);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('auth:token-expired', handleTokenExpired);
    };
  }, []);

  // Poll for cross-check completion on MCC / Construction Loan sub-projects so the
  // results view refreshes once the backend marks step 3 as completed.
  React.useEffect(() => {
    if (!selectedSubProjectId || !isAuthenticated) {
      return;
    }

    let isActive = true;
    let pollInterval: NodeJS.Timeout | null = null;
    // Require N consecutive "completed" polls before refreshing, so we don't react
    // to a stale completed status from a previous run while a new cross-check is
    // still being kicked off in the background.
    let consecutiveCompletedCount = 0;

    const pollCrossCheckProgress = async () => {
      try {
        const response = await apiService.getSubProjectDetails(selectedSubProjectId);

        if (!isActive) return;

        if (response.success && response.data) {
          const crossCheckProgress = response.data.cross_check_progress;
          if (crossCheckProgress && crossCheckProgress.workflow_step === 3 && crossCheckProgress.status === 'completed') {
            consecutiveCompletedCount += 1;
            if (consecutiveCompletedCount >= 3) {
              if (pollInterval) {
                clearInterval(pollInterval);
                pollInterval = null;
              }
              setRefreshClausesKey(prev => prev + 1);
            }
          } else {
            consecutiveCompletedCount = 0;
          }
        }
      } catch (error) {
        console.error('[TaskPane] Error polling cross-check progress:', error);
      }
    };

    pollCrossCheckProgress();
    pollInterval = setInterval(pollCrossCheckProgress, 2000);

    return () => {
      isActive = false;
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [selectedSubProjectId, isAuthenticated]);

  const handleSubProjectSelect = (subProjectId: string) => {
    setSelectedSubProjectId(subProjectId);
    if (subProjectId) {
      setShowMccCreateFlow(false); // Existing sub-project chosen — hide guided create wizard
      setViewMode('clauses');
    }
  };

  const handleProjectSelect = (projectId: string) => {
    setSelectedProjectId(projectId);
    setShowMccCreateFlow(false);
    setFilenameMatchOptions(null);
    setSelectedFilenameMatchProjectId(null);
    if (projectId !== selectedProjectId) {
      setSelectedSubProjectId('');
    }
  };

  const handleLoginSuccess = () => {
    const t = apiService.getCurrentAuthToken();
    console.log('[Courtyard][AuthTrace] handleLoginSuccess', {
      tokenPresent: !!t,
      tokenFingerprint: t
        ? t.length <= 24
          ? `len=${t.length}`
          : `len=${t.length} head=${t.slice(0, 10)}…`
        : null,
      selectedSubProjectId: selectedSubProjectId || '(none)',
      selectedProjectId: selectedProjectId || '(none)',
      viewMode,
      solutionsSubView,
    });
    setIsAuthenticated(true);
  };

  const handleSignOut = React.useCallback(async () => {
    try {
      await signOutFirebase();
    } catch (err) {
      console.warn('[TaskPane] Firebase sign out failed:', err);
    }
    apiService.clearAuthToken();
    setIsAuthenticated(false);
    setViewMode('review');
    setSolutionsSubView('list');
    setSelectedProjectId('');
    setSelectedSubProjectId('');
    pendingReturnTabRef.current = null;
  }, []);

  // When switching between solutions, clear project/sub-project selection so the correct list is shown
  const prevSolutionRef = React.useRef<SolutionsSubView>(solutionsSubView);
  React.useEffect(() => {
    if (solutionsSubView === 'list') {
      setFilenameMatchLoading(false);
    }
    if (prevSolutionRef.current !== solutionsSubView && solutionsSubView !== 'list') {
      setSelectedProjectId('');
      setSelectedSubProjectId('');
      setShowMccCreateFlow(false);
      autoDetectAttemptedRef.current = false;
      if (isCrossCheckFilenameInferenceSolution(solutionsSubView)) {
        setFilenameMatchLoading(true);
      } else {
        setFilenameMatchLoading(false);
      }
      prevSolutionRef.current = solutionsSubView;
    } else if (solutionsSubView !== 'list') {
      prevSolutionRef.current = solutionsSubView;
    }
  }, [solutionsSubView]);

  // Auto-detect sub-project by matching the open Word document filename
  React.useEffect(() => {
    // #region agent log
    const skipReason =
      !isAuthenticated ? 'not_authenticated' :
      viewMode !== 'clauses' ? 'view_not_clauses' :
      !isCrossCheckFilenameInferenceSolution(solutionsSubView) ? 'not_cross_check_filename_solution' :
      selectedSubProjectId ? 'already_has_sub_project' :
      autoDetectAttemptedRef.current ? 'already_attempted' : null;
    fetch('http://127.0.0.1:7243/ingest/5821ff2a-aed1-4131-8717-1f200da798aa',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TaskPane.tsx:auto-detect-effect',message:'auto-detect effect run',data:{skipReason,viewMode,solutionsSubView,selectedSubProjectId:selectedSubProjectId||'(empty)',attempted:autoDetectAttemptedRef.current},timestamp:Date.now(),hypothesisId:'H1_H5'})}).catch(()=>{});
    // #endregion
    if (
      !isAuthenticated ||
      viewMode !== 'clauses' ||
      !isCrossCheckFilenameInferenceSolution(solutionsSubView) ||
      selectedSubProjectId
    ) {
      setFilenameMatchLoading(false);
      return;
    }
    if (autoDetectAttemptedRef.current) {
      return;
    }

    autoDetectAttemptedRef.current = true;
    setFilenameMatchLoading(true);
    let cancelled = false;

    (async () => {
      try {
        const filename = await DocumentExporter.getDocumentName();
        // #region agent log
        fetch('http://127.0.0.1:7243/ingest/5821ff2a-aed1-4131-8717-1f200da798aa',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TaskPane.tsx:after-getDocumentName',message:'filename from Word',data:{filename:filename||'(empty)',isFallback:filename==='document.docx'},timestamp:Date.now(),hypothesisId:'H2'})}).catch(()=>{});
        // #endregion
        if (cancelled || !filename || filename === 'document.docx') {
          return;
        }

        console.log(`[TaskPane] Auto-detect: resolving filename "${filename}"`);
        const response = await apiService.getSubProjectByFilename(filename);
        if (cancelled) return;

        // #region agent log
        const matchCount = response.data?.matches?.length ?? 0;
        fetch('http://127.0.0.1:7243/ingest/5821ff2a-aed1-4131-8717-1f200da798aa',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TaskPane.tsx:after-api',message:'API response',data:{success:response.success,matchCount,singleMatch:matchCount===1?response.data?.matches?.[0]:null},timestamp:Date.now(),hypothesisId:'H3_H4'})}).catch(()=>{});
        // #endregion
        if (response.success && response.data?.matches?.length === 1) {
          const match = response.data.matches[0];
          console.log(`[TaskPane] Auto-detect: unique match found – project=${match.project_name}, sub-project=${match.sub_project_name}`);
          // #region agent log
          fetch('http://127.0.0.1:7243/ingest/5821ff2a-aed1-4131-8717-1f200da798aa',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TaskPane.tsx:setting-selection',message:'setting project and sub-project',data:{project_id:match.project_id,sub_project_id:match.sub_project_id},timestamp:Date.now(),hypothesisId:'H4'})}).catch(()=>{});
          // #endregion
          setFilenameMatchOptions(null);
          setSelectedProjectId(match.project_id);
          setSelectedSubProjectId(match.sub_project_id);
        } else if ((response.data?.matches?.length ?? 0) > 1) {
          console.log(`[TaskPane] Auto-detect: ${response.data!.matches.length} matches found – showing disambiguation`);
          setFilenameMatchOptions(response.data!.matches);
        } else {
          setFilenameMatchOptions(null);
          console.log('[TaskPane] Auto-detect: no matches found');
        }
      } catch (err) {
        console.error('[TaskPane] Auto-detect failed:', err);
      } finally {
        setFilenameMatchLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, viewMode, solutionsSubView, selectedSubProjectId, autoDetectTrigger]);

  // Reset auto-detect flag and clear filename matches when user leaves MCC / Construction Loan
  React.useEffect(() => {
    if (!isCrossCheckFilenameInferenceSolution(solutionsSubView)) {
      autoDetectAttemptedRef.current = false;
      setFilenameMatchOptions(null);
      setSelectedFilenameMatchProjectId(null);
      setShowManualProjectSelect(false);
      setFilenameMatchLoading(false);
    }
  }, [solutionsSubView]);

  // Show login if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="task-pane task-pane--login">
        <Login onLoginSuccess={handleLoginSuccess} />
      </div>
    );
  }

  return (
    <div className="task-pane">
      <div className="task-pane-content">
        {(viewMode === 'review' || reviewKeepAlive) && (
          <div
            className="task-pane-view-root"
            hidden={viewMode !== 'review'}
            aria-hidden={viewMode !== 'review'}
          >
            <ReviewView onReviewContextCommitted={handleReviewContextCommitted} />
          </div>
        )}

        {viewMode === 'editor' && (
          <EditorView
            chatService={chatService}
            onRequireReview={() => goToReviewForContext('editor')}
          />
        )}

        {viewMode === 'agent' && (
          <AgentView
            parentProjectId={selectedProjectId || undefined}
            workflowHint={
              solutionsSubView === 'construction_loan'
                ? 'construction_loan'
                : solutionsSubView === 'master_cross_checks'
                ? 'real_estate'
                : undefined
            }
            onRequireReview={() => goToReviewForContext('agent')}
          />
        )}

        {viewMode === 'ask' && (
          <AskView onRequireReview={() => goToReviewForContext('ask')} />
        )}
        
        {viewMode === 'clauses' && (
          <div
            ref={clausesViewRef}
            className={`clauses-view${mccBesidePdfDocked ? ' clauses-view--mcc-beside-split' : ''}`}
            style={
              mccBesidePdfDocked
                ? ({ ['--clauses-mcc-pdf-pct' as string]: `${mccBesidePdfPct}%` } as React.CSSProperties)
                : undefined
            }
          >
            {solutionsSubView === 'list' ? (
              <SolutionsGrid
                onSelect={(id) => {
                  setSolutionsSubView(id);
                  if (isCrossCheckFilenameInferenceSolution(id)) {
                    setFilenameMatchLoading(true);
                  } else {
                    setFilenameMatchLoading(false);
                  }
                }}
              />
            ) : (
              <>
                {mccBesidePdfDocked && isCrossCheckFilenameInferenceSolution(solutionsSubView) && (
                  <>
                    <div ref={mccPdfBesideDockRef} className="clauses-view__mcc-pdf-dock" />
                    <div ref={mccBesideHandleSlotRef} className="clauses-view__mcc-beside-handle-slot" />
                  </>
                )}
                <div
                  ref={isCrossCheckFilenameInferenceSolution(solutionsSubView) ? mccBesideRightStackRef : undefined}
                  className={
                    isCrossCheckFilenameInferenceSolution(solutionsSubView) ? 'clauses-view__mcc-cc-shell' : undefined
                  }
                >
                {(!isCrossCheckFilenameInferenceSolution(solutionsSubView) ||
                  showManualProjectSelect ||
                  !!selectedProjectId) && (
                  <ProjectSelector
                    solutionId={solutionsSubView as SolutionId}
                    onSubProjectSelect={handleSubProjectSelect}
                    onProjectSelect={handleProjectSelect}
                    onCreateNewSubProject={
                      (solutionsSubView === 'master_cross_checks' || solutionsSubView === 'construction_loan') &&
                      selectedProjectId
                        ? () => setShowMccCreateFlow(true)
                        : undefined
                    }
                    guidedSetupActive={showMccCreateFlow}
                    selectedSubProjectId={selectedSubProjectId}
                    selectedProjectId={selectedProjectId}
                    refreshTrigger={subProjectRefreshTrigger}
                  />
                )}
                {selectedSubProjectId ? (
                  (solutionsSubView === 'master_cross_checks' || solutionsSubView === 'construction_loan') ? (
                    <CrossCheckResultsView
                      key={refreshClausesKey}
                      subProjectId={selectedSubProjectId}
                      projectId={selectedProjectId}
                      pdfBesideDockRef={mccPdfBesideDockRef}
                      besideHandleSlotRef={mccBesideHandleSlotRef}
                      besideResizeOuterRef={clausesViewRef}
                      besidePdfChromeInDock={mccBesidePdfDocked}
                      onMccBesidePdfLayoutChange={handleMccBesidePdfLayoutChange}
                      workflowScrollRequestToken={mccWorkflowScrollRequestToken}
                      besideRightScrollRef={mccBesideRightStackRef}
                    />
                  ) : null
                ) : selectedProjectId ? (
                  <>
                    {!selectedSubProjectId && (
                      <>
                        {(solutionsSubView === 'master_cross_checks' || solutionsSubView === 'construction_loan') &&
                          !showMccCreateFlow && (
                          <div className="clauses-placeholder">
                            <p>
                              Select an existing sub-project above, or use the same guided setup as Master Cross Checks
                              to create a new client project from this document.
                            </p>
                            <button
                              type="button"
                              className="clauses-manual-toggle clauses-guided-create"
                              onClick={() => setShowMccCreateFlow(true)}
                            >
                              New client project (guided setup)
                            </button>
                            {isCrossCheckFilenameInferenceSolution(solutionsSubView) && (
                              <button
                                type="button"
                                className="clauses-manual-toggle"
                                onClick={() => {
                                  autoDetectAttemptedRef.current = false;
                                  setFilenameMatchLoading(true);
                                  setAutoDetectTrigger((t) => t + 1);
                                  setShowManualProjectSelect(false);
                                  setSelectedProjectId('');
                                  setSelectedSubProjectId('');
                                }}
                              >
                                Show suggested projects
                              </button>
                            )}
                          </div>
                        )}
                        {crossCheckStartError && !showMccCreateFlow && (
                          <div className="error-message" style={{ margin: '8px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <span>{crossCheckStartError}</span>
                            <button
                              onClick={() => setCrossCheckStartError(null)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 0 0 8px', fontSize: '14px', lineHeight: 1 }}
                              aria-label="Dismiss error"
                            >
                              ✕
                            </button>
                          </div>
                        )}
                        {(solutionsSubView === 'master_cross_checks' || solutionsSubView === 'construction_loan') &&
                          showMccCreateFlow && (
                          <div className="clauses-guided-initiation">
                            <MasterCrossChecksInitiation
                              projectId={selectedProjectId}
                              workflowVariant={
                                solutionsSubView === 'construction_loan' ? 'construction_loan' : 'master_cross_checks'
                              }
                              onComplete={(subProjectId) => {
                                setShowMccCreateFlow(false);
                                setSelectedSubProjectId(subProjectId);
                                setSubProjectRefreshTrigger((t) => t + 1);
                                setRefreshClausesKey((k) => k + 1);
                              }}
                              onCancel={() => setShowMccCreateFlow(false)}
                              onError={(errMsg) => {
                                // The user was already navigated to the results view (by onComplete),
                                // so we navigate back to the sub-project list and show the error.
                                setSelectedSubProjectId('');
                                setShowMccCreateFlow(false);
                                setCrossCheckStartError(errMsg);
                              }}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {(solutionsSubView === 'master_cross_checks' || solutionsSubView === 'construction_loan') && (
                      <div className="clauses-load-divider" aria-hidden />
                    )}
                    {showManualProjectSelect ? (
                        <div className="clauses-placeholder">
                          <p>Select a project above to get started</p>
                          <button
                            type="button"
                            className="clauses-manual-toggle"
                            onClick={() => {
                              autoDetectAttemptedRef.current = false;
                              setFilenameMatchLoading(true);
                              setAutoDetectTrigger((t) => t + 1);
                              setShowManualProjectSelect(false);
                            }}
                          >
                            Show suggested projects
                          </button>
                        </div>
                      ) : filenameMatchLoading ? (
                        <div
                          className="clauses-placeholder clauses-filename-match-loading"
                          aria-busy="true"
                          aria-live="polite"
                        >
                          <div className="filename-match-spinner" role="status" />
                          <p>Looking for a matching project for this document…</p>
                        </div>
                      ) : filenameMatchOptions && filenameMatchOptions.length > 0 ? (
                        <div className="clauses-followup">
                          {selectedFilenameMatchProjectId == null ? (
                            <>
                              <h3 className="clauses-question-text">What project does this document belong to?</h3>
                              <div className="clauses-options-list">
                                {filenameMatchUniqueProjects.map((p) => (
                                  <button
                                    key={p.project_id}
                                    type="button"
                                    className="clauses-option-item"
                                    onClick={() => setSelectedFilenameMatchProjectId(p.project_id)}
                                  >
                                    <span className="clauses-option-project">{p.project_name}</span>
                                  </button>
                                ))}
                              </div>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                className="clauses-back-link"
                                onClick={() => setSelectedFilenameMatchProjectId(null)}
                              >
                                ← Back
                              </button>
                              <h3 className="clauses-question-text">What sub-project?</h3>
                              <div className="clauses-options-list">
                                {filenameMatchSubProjects.map((m) => (
                                  <button
                                    key={m.sub_project_id}
                                    type="button"
                                    className="clauses-option-item"
                                    onClick={() => {
                                      setFilenameMatchOptions(null);
                                      setSelectedFilenameMatchProjectId(null);
                                      setSelectedProjectId(m.project_id);
                                      setSelectedSubProjectId(m.sub_project_id);
                                    }}
                                  >
                                    <span className="clauses-option-sub">{m.sub_project_name}</span>
                                  </button>
                                ))}
                              </div>
                            </>
                          )}
                          <button
                            type="button"
                            className="clauses-manual-toggle"
                            onClick={() => {
                              setFilenameMatchLoading(false);
                              setShowManualProjectSelect(true);
                            }}
                          >
                            Choose project manually
                          </button>
                        </div>
                      ) : (
                        <div className="clauses-placeholder">
                          <p>No matching project found for this document.</p>
                          <button
                            type="button"
                            className="clauses-manual-toggle"
                            onClick={() => {
                              setFilenameMatchLoading(false);
                              setShowManualProjectSelect(true);
                            }}
                          >
                            Choose project manually
                          </button>
                        </div>
                      )}
                  </>
                )}
                </div>
              </>
            )}
          </div>
        )}
        
        {viewMode === 'proofread' && <ProofreadView />}
      </div>

      <BottomNav
        activeTab={viewMode}
        onTabChange={(tab) => {
          if (tab !== 'review' && !hasReviewPartyContextComplete()) {
            setReviewKeepAlive(true);
          }
          setViewMode(tab);
          if (tab === 'clauses') setSolutionsSubView('list');
        }}
        onSignOut={() => {
          void handleSignOut();
        }}
      />
    </div>
  );
};

export default TaskPane;

